import React, { useCallback, Fragment } from 'react';
// eslint-disable-next-line jira/react-magnetic-di/no-unneeded
import { di } from 'react-magnetic-di';
import { handleAnalytics } from '@atlassian/jira-command-palette-common/src/common/utils/handle-analytics/index.tsx';
import { useQuickSearchCommand } from '@atlassian/jira-command-palette-quick-search/src/ui/use-quick-search-command/index.tsx';
import { useAskRovoCommand } from '@atlassian/jira-command-palette-ask-rovo/src/ui/use-ask-rovo-command/index.tsx';
import { useCommandPaletteRegistry } from '@atlassian/jira-command-palette-registry/src/controllers/use-command-palette-registry/index.tsx';
import type { AnalyticsAttributes } from '@atlassian/jira-command-palette/src/common/types/analytics/index.tsx';
import { useCommandPaletteIsOpen } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import { CommandPaletteModal as CommandPalette } from '@atlassian/jira-command-palette/src/ui/modal/index.tsx';
import type { CommandPaletteModalProps } from '@atlassian/jira-command-palette/src/ui/modal/types.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ForgeCommandPaletteCommandsWithFF } from '@atlassian/jira-forge-command-palette/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { CONTAINER_TEST_ID } from '../common/constants.tsx';
import { useCloseDotMenu } from '../controllers/use-close-dot-menu/index.tsx';
import { useCompalBridge } from '../controllers/use-compal-bridge/index.tsx';
import { useDisableCompalIfEmbed } from '../controllers/use-disable-compal-embed/index.tsx';
import { useRegisterDismissCommand } from '../controllers/use-register-dismiss-command/index.tsx';
import { useRegisterSections } from '../controllers/use-register-sections/index.tsx';
import { FooterFeedbackContent } from './footer-feedback-content/index.tsx';
import { useRegisterInitialCommands } from './initial-commands/index.tsx';
import { messages } from './messages.tsx';
import { RegisterDynamicCommands } from './register-dynamic-commands/index.tsx';

export const CommandPaletteIntegration = (props: CommandPaletteModalProps): React.JSX.Element => {
	// eslint-disable-next-line jira/react-magnetic-di/no-unneeded
	di(ShortcutScope);
	const { formatMessage } = useIntl();
	const [{ isOpen }] = useCommandPaletteIsOpen();
	const { quickSearchCommand } = useQuickSearchCommand({ keepSearch: true });

	// eslint-disable-next-line  react-hooks/rules-of-hooks
	const askRovoCommand = fg('jjj_ask_rovo_compal_v1') ? useAskRovoCommand() : null;

	useCommandPaletteRegistry();

	const onErrorAnalytics = useCallback(
		(
			attribution: string | undefined,
			error: Error | undefined,
			id: string,
			attributes?: AnalyticsAttributes,
		) => {
			fireErrorAnalytics({
				error,
				meta: {
					id: `jira.commandPalette.${id}`,
					packageName: 'jiraCommandPaletteIntegration',
					teamName: attribution || 'deliveroo',
				},
				attributes,
			});
		},
		[],
	);

	useRegisterSections();

	useRegisterDismissCommand();

	useCloseDotMenu();

	useDisableCompalIfEmbed();

	useRegisterInitialCommands();

	useCompalBridge();

	const Wrapper = isOpen ? ShortcutScope : Fragment;
	return (
		<>
			<Wrapper>
				<div data-testid={CONTAINER_TEST_ID}>
					<CommandPalette
						defaultFooterContent={<FooterFeedbackContent />}
						onAnalytics={handleAnalytics}
						onErrorAnalytics={onErrorAnalytics}
						defaultAriaLabel={formatMessage(messages.defaultSearchInputAriaLabel)}
						defaultPlaceholder={formatMessage(messages.defaultPlaceholder)}
						defaultFallback={{
							onError: {
								label: formatMessage(messages.onErrorFallback),
							},
							onNoResults: {
								label: formatMessage(messages.noResultsFound),
							},
						}}
						rootFallback={{
							label: formatMessage(messages.noResultsFound),
							...(fg('jjj_ask_rovo_compal_v1')
								? { fallbackCommands: [quickSearchCommand, askRovoCommand].filter(Boolean) }
								: // when cleaning up jjj_ask_rovo_compal_v1 remove fallbackCommand and just leave fallbackCommands
									{ fallbackCommand: quickSearchCommand }),
							fallbackSection: {
								name: formatMessage(messages.recommendedSection),
							},
						}}
						{...props}
					/>
					<RegisterDynamicCommands />
				</div>
			</Wrapper>
			<ForgeCommandPaletteCommandsWithFF />
		</>
	);
};
